'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ExternalLink, CheckCircle, Hospital, TrendingUp, Clock, Shield } from 'lucide-react';
import { Policy, INSURERS, formatCurrency, formatPremium, getCSRBadgeClass } from '@/lib/policyData';
import DisclaimerPopup from '@/components/ui/DisclaimerPopup';

interface PolicyCardProps {
  policy: Policy;
  onSelect?: (id: string) => void;
  isSelected?: boolean;
  selectionCount?: number;
  showSelectButton?: boolean;
}

export default function PolicyCard({
  policy,
  onSelect,
  isSelected = false,
  selectionCount = 0,
  showSelectButton = false,
}: PolicyCardProps) {
  const insurer = INSURERS[policy.insurer];
  const csrClass = getCSRBadgeClass(policy.claimSettlementRatio);
  const canSelect = selectionCount < 3 || isSelected;
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  return (
    <>
      <div
        className={`policy-card flex flex-col overflow-hidden ${
          isSelected ? 'ring-2 ring-primary' : ''
        }`}
      >
        {/* Top strip */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-border/50">
          <div className="flex items-center gap-3">
            {/* Insurer badge */}
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center font-extrabold text-xs flex-shrink-0 ${insurer.bgColor} ${insurer.color}`}
            >
              {insurer.abbr}
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium">{policy.insurerName}</p>
              <p className="text-sm font-bold text-foreground leading-tight">{policy.planName}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {policy.tag && (
              <span className="text-2xs font-semibold px-2 py-0.5 bg-primary/10 text-primary rounded-full uppercase tracking-wide">
                {policy.tag}
              </span>
            )}
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${csrClass}`}
            >
              <TrendingUp className="w-3 h-3" />
              {policy.claimSettlementRatio}% CSR
            </span>
          </div>
        </div>

        {/* Premium & Coverage */}
        <div className="grid grid-cols-2 gap-3 px-5 py-4 bg-muted/30">
          <div>
            <p className="text-xs text-muted-foreground font-medium mb-0.5">Annual Premium</p>
            <p className="text-xl font-extrabold text-foreground font-tabular">
              {formatPremium(policy.premium)}
            </p>
            <p className="text-2xs text-muted-foreground">per year</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground font-medium mb-0.5">Coverage</p>
            <p className="text-xl font-extrabold text-foreground font-tabular">
              {formatCurrency(policy.coverageAmount)}
            </p>
            <p className="text-2xs text-muted-foreground">sum insured</p>
          </div>
        </div>

        {/* Key stats row */}
        <div className="flex items-center gap-4 px-5 py-3 border-b border-border/50 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Hospital className="w-3.5 h-3.5 text-primary/70" />
            <span className="font-medium text-foreground">
              {policy.networkHospitals >= 1000
                ? `${(policy.networkHospitals / 1000).toFixed(0)}K+`
                : policy.networkHospitals}
            </span>
            <span>hospitals</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="w-3.5 h-3.5 text-primary/70" />
            <span className="font-medium text-foreground">{policy.waitingPeriodYears} yr</span>
            <span>waiting</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Shield className="w-3.5 h-3.5 text-primary/70" />
            <span className="font-medium text-foreground">{policy.policyType}</span>
          </div>
        </div>

        {/* Highlights */}
        <div className="px-5 py-4 flex-1">
          <ul className="flex flex-col gap-2">
            {policy.highlights.slice(0, 3).map((h, i) => (
              <li
                key={`highlight-${policy.id}-${i}`}
                className="flex items-start gap-2 text-xs text-muted-foreground"
              >
                <CheckCircle className="w-3.5 h-3.5 text-[var(--success)] flex-shrink-0 mt-0.5" />
                <span>{h}</span>
              </li>
            ))}
          </ul>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {policy.restoration && (
              <span className="text-2xs font-medium px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full border border-teal-200">
                Restoration
              </span>
            )}
            {policy.maternity && (
              <span className="text-2xs font-medium px-2 py-0.5 bg-pink-50 text-pink-700 rounded-full border border-pink-200">
                Maternity
              </span>
            )}
            {policy.opd && (
              <span className="text-2xs font-medium px-2 py-0.5 bg-violet-50 text-violet-700 rounded-full border border-violet-200">
                OPD Cover
              </span>
            )}
            {policy.criticalIllness && (
              <span className="text-2xs font-medium px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full border border-amber-200">
                Critical Illness
              </span>
            )}
            {policy.annualHealthCheckup && (
              <span className="text-2xs font-medium px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full border border-blue-200">
                Free Checkup
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 py-4 border-t border-border/50 flex items-center gap-2">
          {showSelectButton && (
            <button
              onClick={() => onSelect?.(policy.id)}
              disabled={!canSelect && !isSelected}
              className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold border transition-all duration-150 active:scale-95 ${
                isSelected
                  ? 'bg-primary text-primary-foreground border-primary'
                  : canSelect
                  ? 'bg-muted text-foreground border-border hover:bg-primary/10 hover:border-primary hover:text-primary'
                  : 'bg-muted text-muted-foreground border-border cursor-not-allowed opacity-50'
              }`}
            >
              {isSelected ? '✓ Selected' : 'Select to Compare'}
            </button>
          )}
          <Link
            href={`/policy-detail?id=${policy.id}`}
            className={`${showSelectButton ? '' : 'flex-1'} btn-outline py-2 px-3 text-xs justify-center`}
          >
            View Details
          </Link>
          <button
            onClick={() => setShowDisclaimer(true)}
            className="flex-1 btn-primary py-2 px-3 text-xs justify-center"
          >
            Buy Now
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      </div>

      <DisclaimerPopup
        isOpen={showDisclaimer}
        onClose={() => setShowDisclaimer(false)}
        buyUrl={policy.buyUrl}
        insurerName={insurer.name}
      />
    </>
  );
}